import { ApiError } from "@/lib/api-client";

// Maps a failed checkout-session POST to the inline banner copy. Same shape as
// the house error mappers (`legacy-cert-error.ts`, `clients/client-error.ts`,
// `account/change-email-error.ts`): branch ONLY on the structured `error.code`,
// never on the server's `message`/`details` text, and always render our own copy.
//
// Story 10.15 extracted this from `SubscriptionSection.tsx`, where it branched on
// BILL-006 alone and flattened everything else into the retry sentence (DW-419).
// That was defensible while the only caller was a legacy clinician; it stopped
// being defensible when the LearnWorlds variant gained the same button, because
// BILL-003 became reachable in practice — a `learnworlds_new` account that never
// held the system bundled grant is refused PERMANENTLY, and "try again in a
// moment" is then a lie told to the population most likely to hit it.
//
// STATUS FALLBACKS ARE DELIBERATELY NARROW. The sibling mappers pair every code
// with `|| error.status === N` because each of their codes owns its status. This
// endpoint's do not: 403 is shared by BILL-003 and BILL-004, and 409 by BILL-005
// and BILL-006. A status fallback there would hand a cert-verification refusal
// the checkout-cohort sentence, or an already-subscribed clinician the org-seat
// one. Only 503 and 0 (network) identify one outcome each, so only they carry the
// second guard.
//
// [Source: src/application/billing/create_checkout_session/command.py —
//  CheckoutNotAvailableException (403 BILL-003), CertNotVerifiedException
//  (403 BILL-004), SubscriptionExistsException (409 BILL-005), SeatCoveredException
//  (409 BILL-006), CheckoutNotConfiguredException (503 BILL-503); api-client
//  NETWORK_ERROR (status 0).]

const GENERIC =
  "We couldn't start checkout just now. Please try again in a moment.";

// DW-418 (adjacent half) — 409 BILL-006 (`org_seat_excludes_subscription`) is a
// PERMANENT refusal, so the copy must not promise a retry. A seat holder's access
// comes from their organization's contract (FR51 "seat wins"), and nothing
// releases the seat on lapse — no amount of retrying can make this checkout
// succeed. The sentence says only what holds in ALL FOUR contract windows the
// server gate covers (live / lapsed / not started / NULL dates) and names no
// organization, matching the server's deliberately generic body; it therefore
// also directs the clinician at no one it cannot name.
const ORG_SEAT =
  "This account holds an organization seat, so an individual subscription " +
  "doesn't apply to it. Your access follows your organization's contract and " +
  "is managed by its administrator.";

// 403 BILL-003 — PERMANENT, and the reason this module exists. The server folds
// three conditions into this one code on purpose (an origin outside the checkout
// cohorts; a `learnworlds_new` account that never held the system bundled grant;
// a `learnworlds_new` account that has not completed calibration, FR47),
// because naming WHICH one an account fails is the enumeration surface the 16.3
// LEGACY-003 precedent closes. Our copy honours that: it states the outcome and
// points at the one channel that can change it, and it enumerates nothing.
const NOT_AVAILABLE =
  "Checkout isn't available for this account. Contact CITS support to sort " +
  "out how your subscription should be set up.";

// 409 BILL-005 — a live subscription already exists, so there is nothing to buy.
// Not a failure the clinician caused and not one a retry clears; the useful
// action is to look at the state the page is already showing.
const ALREADY_SUBSCRIBED =
  "This account already has a live subscription, so there's nothing to renew " +
  "right now. Refresh the page to see its current state.";

// 503 BILL-503 — Stripe key or price unset (the server fails CLOSED rather than
// minting a dead session). Genuinely temporary from the clinician's side, but it
// is an outage rather than a hiccup, so the copy does not suggest an immediate
// retry the way the generic sentence does.
const UNAVAILABLE =
  "Checkout is temporarily unavailable. Please try again later — nothing has " +
  "been charged.";

const NETWORK =
  "We couldn't reach the server. Check your connection and try again.";

// 403 BILL-004 — the endpoint's own cert gate, and a defence in depth rather
// than the first line: the AUTH gate admits a stage-2 legacy clinician to the
// mint on the strength of their token's `cert_verified` claim and 403s stage-1
// with LEGACY-001 before the endpoint is reached. The endpoint then reads the
// SAME claim (`command.py` step b — "claim read, no DB"), so BILL-004 is what a
// caller the gate did not pin sees — an account-active legacy clinician whose
// token still says the certificate is unverified. FR39's ordering is the reason
// either way, so the copy names the ordering — and it is a WAIT, not a permanent
// refusal, which is exactly what the generic "try again in a moment" sentence
// got wrong.
const CERT_NOT_VERIFIED =
  "Your certificate hasn't been verified yet. Payment opens as soon as CITS " +
  "approves it — you'll get an email when that happens.";

// 403 LEGACY-001 — the AUTH gate, one level above the endpoint: a stage-1
// legacy clinician (certificate still under review) reaching the checkout mint,
// which happens when a Profile left open across an approval still shows the
// button. Same substance as BILL-004 and deliberately the same sentence: the
// two codes are the same fact answered by two different layers, and giving them
// different copy would tell one clinician two stories about one account.
//
// 403 LEGACY-002 is its stage-2 twin (approved, awaiting payment). It is no
// longer reachable FROM THIS ENDPOINT — that is precisely the population Story
// 16.7's allow-list admits — but it is mapped rather than left generic, because
// "your payment hasn't gone through" is the one sentence that must never
// degrade into "try again in a moment" on a payment surface.
const PAYMENT_PENDING =
  "Your account isn't enabled yet. Complete payment to unlock calibration.";

/**
 * Map a failed checkout-session POST to its inline banner sentence.
 *
 * Everything not named here — AuthExpiredError, unknown throwables — keeps the
 * generic retry copy.
 *
 * DW-419 CLOSED by Story 16.7. The deferral was explicit: BILL-004 and
 * LEGACY-001/002 were left flattened into the retry sentence while the auth
 * gate refused the whole route for the gated cohort, on the understanding that
 * 16.7's allow-list change is what makes them reachable. It has, so they are
 * mapped.
 */
export function toCheckoutErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "BILL-006") return ORG_SEAT;
    if (error.code === "BILL-003") return NOT_AVAILABLE;
    if (error.code === "BILL-004") return CERT_NOT_VERIFIED;
    if (error.code === "BILL-005") return ALREADY_SUBSCRIBED;
    if (error.code === "LEGACY-001") return CERT_NOT_VERIFIED;
    if (error.code === "LEGACY-002") return PAYMENT_PENDING;
    if (error.code === "BILL-503" || error.status === 503) return UNAVAILABLE;
    if (error.code === "NETWORK_ERROR" || error.status === 0) return NETWORK;
  }
  return GENERIC;
}
